import { useState } from 'react';
import { api } from '../lib/api.js';
import { Modal, StatusPill, useToast, Field } from './ui.jsx';
import { relativeTime, formatDate } from '../lib/format.js';

/**
 * Interactive complaint view for the patient: read the full thread, edit while
 * it is still open, withdraw an open complaint, or reopen one that was resolved
 * or dismissed. Every action calls back with the updated record so the list
 * stays in step without a refetch.
 */
export default function ComplaintDetail({ complaint, onClose, onChange }) {
  const toast = useToast();
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ subject: complaint.subject, message: complaint.message });

  const act = async (fn, label) => {
    setBusy(label);
    try {
      const updated = await fn();
      onChange(updated);
      toast.success(`Complaint ${label}.`);
      setEditing(false);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy('');
    }
  };

  const saveEdit = () =>
    act(() => api.put(`/api/complaints/${complaint.id}`, draft), 'updated');
  const withdraw = () =>
    act(() => api.post(`/api/complaints/${complaint.id}/status`, { action: 'withdraw' }), 'withdrawn');
  const reopen = () =>
    act(() => api.post(`/api/complaints/${complaint.id}/status`, { action: 'reopen' }), 'reopened');

  const isOpen = complaint.status === 'open';

  return (
    <Modal
      title="Complaint"
      onClose={onClose}
      size={520}
      footer={
        <>
          {isOpen && !editing && (
            <button className="btn btn-ghost" onClick={() => setEditing(true)} disabled={!!busy}>
              Edit
            </button>
          )}
          {editing && (
            <>
              <button className="btn btn-ghost" onClick={() => setEditing(false)} disabled={!!busy}>
                Cancel edit
              </button>
              <button
                className="btn btn-primary"
                onClick={saveEdit}
                disabled={!draft.subject.trim() || !draft.message.trim() || !!busy}
              >
                Save changes
              </button>
            </>
          )}
          {!editing && isOpen && (
            <button className="btn btn-danger" onClick={withdraw} disabled={!!busy}>
              {busy === 'withdrawn' ? <span className="spinner" /> : 'Withdraw complaint'}
            </button>
          )}
          {!editing && !isOpen && (
            <button className="btn btn-primary" onClick={reopen} disabled={!!busy}>
              {busy === 'reopened' ? <span className="spinner" /> : 'Reopen complaint'}
            </button>
          )}
        </>
      }
    >
      {editing ? (
        <>
          <Field label="Subject" id="e-subject">
            <input
              id="e-subject"
              type="text"
              value={draft.subject}
              onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
            />
          </Field>
          <Field label="What happened?" id="e-message">
            <textarea
              id="e-message"
              value={draft.message}
              onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))}
            />
          </Field>
        </>
      ) : (
        <>
          <div className="row-between mb-2">
            <h3 style={{ margin: 0 }}>{complaint.subject}</h3>
            <StatusPill status={complaint.status} />
          </div>
          <p className="tiny dim">Raised {formatDate(complaint.created_at?.slice(0, 10))} · {relativeTime(complaint.created_at)}</p>

          <div className="complaint-thread">
            <div className="complaint-bubble">
              <div className="tiny dim mb-1">You wrote</div>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{complaint.message}</p>
            </div>
            {complaint.resolution && (
              <div className="complaint-bubble admin">
                <div className="tiny dim mb-1">Administrator response</div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{complaint.resolution}</p>
              </div>
            )}
          </div>

          <p className="tiny dim mt-2">
            {isOpen
              ? 'This complaint is open. You can edit it or withdraw it while our team reviews it.'
              : `This complaint was ${complaint.status}. If it is not resolved to your satisfaction, you can reopen it.`}
          </p>
        </>
      )}
    </Modal>
  );
}
